import { createFileRoute, Navigate } from '@tanstack/react-router';
import {
    ArrowLeft,
    CalendarDays,
    MessageSquareText,
    Phone,
} from 'lucide-react';
import { type ReactNode } from 'react';

import {
    type StreamRunNode,
    type StreamRunState,
} from '@apps/web/components/phoebe_chat/streaming_types';
import { LiveStatusRow } from '@apps/web/components/phoebe_chat_view/messages/status_rows';
import {
    derivePhoebeThinkingTip,
    PhoebeShimmerStatus,
} from '@apps/web/components/phoebe_chat_view/run_status/shimmer_status';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import { type OrganizationFeatures } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/phoebe-thinking-tips-mockup')({
    component: PhoebeThinkingTipsMockupRoute,
    head: () => ({
        meta: [
            {
                title: 'Phoebe thinking tips mockup | Phoebe',
            },
        ],
    }),
});

function PhoebeThinkingTipsMockupRoute() {
    if (getRunEnvironment() !== 'development') {
        return <Navigate to="/" replace={true} />;
    }

    return <PhoebeThinkingTipsMockupPage />;
}

export function PhoebeThinkingTipsMockupPage() {
    const thinkingTipsEnabled = useFeatureFlag(
        'phoebe_thinking_tips_enabled',
        PREVIEW_FEATURES,
    );
    const previewTip = derivePhoebeThinkingTip({
        enabled: thinkingTipsEnabled,
        run: MOCK_LONG_RUNNING_RUN,
        tipIndex: 0,
    });

    return (
        <main className="bg-background text-foreground min-h-screen">
            <div className="border-border bg-card flex h-14 items-center justify-between border-b px-5">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex size-8 items-center justify-center rounded-md"
                        aria-label="Back"
                    >
                        <ArrowLeft className="size-4" />
                    </button>
                    <div>
                        <div className="text-sm font-medium">
                            Open Shift Details
                        </div>
                        <div className="text-muted-foreground text-xs">
                            E2E Client - weekday morning coverage
                        </div>
                    </div>
                </div>
                <div className="text-muted-foreground text-xs">
                    phoebe_thinking_tips_enabled:{' '}
                    {thinkingTipsEnabled ? 'on' : 'off'}
                </div>
            </div>
            <div className="grid min-h-[calc(100vh-3.5rem)] grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px]">
                <section className="border-border min-h-0 border-r">
                    <div className="mx-auto flex max-w-5xl flex-col gap-6 px-6 py-6">
                        <div className="grid gap-4 md:grid-cols-3">
                            <OutreachSummaryItem
                                icon={<CalendarDays className="size-4" />}
                                label="Shift"
                                value="Tomorrow, 8:00 AM-4:00 PM"
                            />
                            <OutreachSummaryItem
                                icon={<MessageSquareText className="size-4" />}
                                label="SMS"
                                value="3 caregivers queued"
                            />
                            <OutreachSummaryItem
                                icon={<Phone className="size-4" />}
                                label="Voice"
                                value="Backup step pending"
                            />
                        </div>
                        <section className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h1 className="text-lg font-semibold">
                                    Outreach plan
                                </h1>
                                <span className="border-border text-muted-foreground rounded-md border px-2 py-1 text-xs">
                                    In progress
                                </span>
                            </div>
                            <div className="border-border overflow-hidden rounded-md border">
                                <div className="border-border grid grid-cols-[1fr_120px_120px] border-b px-4 py-2 text-xs font-medium">
                                    <div>Caregiver</div>
                                    <div>Status</div>
                                    <div>Next step</div>
                                </div>
                                {[
                                    ['E2E Caregiver Ada', 'Text sent', 'Wait'],
                                    [
                                        'E2E Caregiver Grace',
                                        'No response',
                                        'Voice',
                                    ],
                                    [
                                        'E2E Caregiver Katherine',
                                        'Queued',
                                        'SMS',
                                    ],
                                ].map(([name, status, step]) => (
                                    <div
                                        key={name}
                                        className="border-border grid grid-cols-[1fr_120px_120px] border-b px-4 py-3 text-sm last:border-b-0"
                                    >
                                        <div className="font-medium">
                                            {name}
                                        </div>
                                        <div className="text-muted-foreground">
                                            {status}
                                        </div>
                                        <div className="text-muted-foreground">
                                            {step}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                        <section className="space-y-3">
                            <h2 className="text-sm font-medium">
                                General agent
                            </h2>
                            <div className="border-border bg-card rounded-md border px-4 py-3">
                                <PhoebeShimmerStatus
                                    label="Thinking"
                                    tip={previewTip}
                                />
                            </div>
                        </section>
                    </div>
                </section>
                <aside className="bg-background flex min-h-[560px] flex-col">
                    <div className="border-border border-b px-4 py-3">
                        <div className="text-sm font-medium">
                            Phoebe outreach agent
                        </div>
                        <div className="text-muted-foreground text-xs">
                            Connected to this outreach
                        </div>
                    </div>
                    <div className="flex min-h-0 flex-1 flex-col justify-end px-4 py-4">
                        <div className="space-y-4">
                            <div className="bg-muted/50 rounded-md px-3 py-2 text-sm">
                                Which caregivers should we try first?
                            </div>
                            <div className="border-border rounded-md border px-3 py-3">
                                <LiveStatusRow
                                    status={{
                                        phase: 'thinking',
                                        label: 'Thinking',
                                        detail: null,
                                        toolName: null,
                                    }}
                                    showSpinner={true}
                                    tip={previewTip}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="border-border border-t p-3">
                        {previewTip ? (
                            <div className="text-muted-foreground rounded-md border px-3 py-2 text-sm">
                                {previewTip}
                            </div>
                        ) : null}
                    </div>
                </aside>
            </div>
        </main>
    );
}

const PREVIEW_FEATURES = {
    phoebe_thinking_tips_enabled: false,
} satisfies OrganizationFeatures;

const MOCK_LONG_RUNNING_RUN: StreamRunState = {
    runId: 'thinking-tips-mockup-run',
    requestTurnId: null,
    startedAt: '2026-05-05T09:00:00.000Z',
    status: 'running',
    orderedNodeIds: ['tool-0', 'tool-1', 'tool-2', 'tool-3'],
    nodesById: {
        'tool-0': makeMockToolNode('tool-0', 0, true),
        'tool-1': makeMockToolNode('tool-1', 1, true),
        'tool-2': makeMockToolNode('tool-2', 2, true),
        'tool-3': makeMockToolNode('tool-3', 3, false),
    },
};

function makeMockToolNode(
    id: string,
    sequence: number,
    isComplete: boolean,
): StreamRunNode {
    return {
        id,
        parentNodeId: null,
        kind: 'tool',
        createdAt: '2026-05-05T09:00:00.000Z',
        sequence,
        content: '',
        phase: 'tool',
        label: null,
        detail: null,
        toolCallId: id,
        toolName: 'mock_tool',
        arguments: null,
        result: null,
        error: null,
        status: null,
        isComplete,
        responseKind: null,
    };
}

function OutreachSummaryItem({
    icon,
    label,
    value,
}: {
    readonly icon: ReactNode;
    readonly label: string;
    readonly value: string;
}) {
    return (
        <div className="border-border bg-card rounded-md border px-4 py-3">
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
                {icon}
                {label}
            </div>
            <div className="mt-2 text-sm font-medium">{value}</div>
        </div>
    );
}
